/**
 * Shift acceptance cutoff — shared between the Admin Configuration Location
 * default (gat-admin) and the per-Shift override (scheduler dialogs).
 *
 * The wire format is always decimal hours (System.Double / decimal), e.g. 0.5.
 * hh:mm is a display format only, so 0.5 reads as "00:30".
 */

/** Upper bound of the selectable range, in hours: one week. */
export const MAX_CUTOFF_HOURS = 168;

/** Lower bound of the selectable range, in hours. */
export const MIN_CUTOFF_HOURS = 0.5;

/**
 * Split a decimal-hours value into the hh / mm parts the input renders.
 * Minutes are always 0 or 30 - the cutoff is set in half-hour steps.
 */
export const splitCutoffHours = (
	value: number
): { hours: number; minutes: number } => {
	const totalMinutes = Math.round((value || 0) * 60);
	return {
		hours: Math.max(0, Math.floor(totalMinutes / 60)),
		minutes: totalMinutes % 60 >= 30 ? 30 : 0,
	};
};

/**
 * Recombine hh / mm into decimal hours, clamped to the selectable range so a
 * typed value can never leave the field outside 00:30 … 168:00.
 */
export const toCutoffHours = (wholeHours: number, minutes: number): number => {
	const safeHours = Number.isFinite(wholeHours)
		? Math.max(0, Math.floor(wholeHours))
		: 0;
	const total = safeHours + (minutes >= 30 ? 0.5 : 0);
	if (total > MAX_CUTOFF_HOURS) {
		return MAX_CUTOFF_HOURS;
	}
	if (total < MIN_CUTOFF_HOURS) {
		return MIN_CUTOFF_HOURS;
	}
	return total;
};

/** Seeded the first time a cutoff is switched on and nothing is inherited. */
export const DEFAULT_CUTOFF_HOURS = 2;

/** 0.5 -> "00:30", 12.5 -> "12:30", 168 -> "168:00" */
export const formatCutoffHours = (hours: number): string => {
	const totalMinutes = Math.round(hours * 60);
	const displayHours = Math.floor(totalMinutes / 60);
	const displayMinutes = totalMinutes % 60;
	return `${String(displayHours).padStart(2, "0")}:${String(
		displayMinutes
	).padStart(2, "0")}`;
};

/** The moment acceptance closes: Shift start minus the cutoff. */
export const getAcceptanceCutoffTime = (
	startTime: string | Date,
	cutoffHours: number
): Date | null => {
	const start = new Date(startTime);
	if (isNaN(start.getTime())) {
		return null;
	}
	return new Date(start.getTime() - Math.round(cutoffHours * 60) * 60 * 1000);
};

/**
 * The minimum a Shift object needs to carry for the cutoff to be resolved.
 * Both fields ship on every Worker/Supervisor shift payload; when the cutoff
 * is off (or zero) acceptance stays open right up to the Shift start.
 */
export interface AcceptanceCutoffShift {
	startDateTime?: string | Date | null;
	isShiftAcceptanceCutoffEnabled?: boolean;
	minHoursBeforeShiftStartToAccept?: number | string | null;
}

/**
 * The moment a Shift stops accepting sign-ups, or null when it never does.
 *
 * Always measured against the Shift's own `startDateTime` - never a Worker's
 * chosen flexi or partial window - so a later slot cannot dodge the cutoff.
 */
export const getShiftAcceptanceCutoffTime = (
	shift?: AcceptanceCutoffShift | null
): Date | null => {
	if (!shift?.isShiftAcceptanceCutoffEnabled || !shift?.startDateTime) {
		return null;
	}
	const hours = Number(shift.minHoursBeforeShiftStartToAccept) || 0;
	if (hours <= 0) {
		return null;
	}
	return getAcceptanceCutoffTime(shift.startDateTime, hours);
};

/** True once the acceptance window has shut. */
export const isClosedForSignup = (
	shift?: AcceptanceCutoffShift | null,
	now: Date = new Date()
): boolean => {
	const cutoff = getShiftAcceptanceCutoffTime(shift);
	return !!cutoff && now.getTime() >= cutoff.getTime();
};

/** How long the acceptance window has left, in whole minutes ceiling. */
export const CLOSING_SOON_MINUTES = 60;

/** True in the last hour before the window shuts - still acceptable, but warn. */
export const isClosingSoonForSignup = (
	shift?: AcceptanceCutoffShift | null,
	now: Date = new Date()
): boolean => {
	const cutoff = getShiftAcceptanceCutoffTime(shift);
	if (!cutoff) {
		return false;
	}
	const minutesLeft = (cutoff.getTime() - now.getTime()) / 60000;
	return minutesLeft > 0 && minutesLeft <= CLOSING_SOON_MINUTES;
};

/** Error code the API returns when an accept lands inside the cutoff window. */
export const ACCEPTANCE_WINDOW_CLOSED_ERROR =
	"Error.SP.WorkerShiftSignup.AcceptanceWindowClosed";

/**
 * A cutoff must leave workers at least this long to accept, otherwise the Shift
 * is published with an acceptance window that is already shut.
 */
export const MIN_CUTOFF_LEAD_MS = 30 * 60 * 1000;

/**
 * True when the resolved close time is in the past, or less than 30 minutes
 * away. Used to block create - an existing Shift can still be tightened.
 */
export const isCutoffClosingTooSoon = (
	startTime: string | Date,
	cutoffHours: number
): boolean => {
	const closesAt = getAcceptanceCutoffTime(startTime, cutoffHours);
	if (!closesAt) {
		return false;
	}
	return closesAt.getTime() < Date.now() + MIN_CUTOFF_LEAD_MS;
};
